import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import firebase from "firebase/compat/app"
import axios from "axios"
import { getEnv } from "../../../../utils/env"
import { getCustomHeaders } from "../../../../modules/mp-utils/generic-utils"
import { GQLApiNamesType } from "./studyPlanExecutors"

const httpLink = createHttpLink({
  uri: ({ getContext }) => {
    const { apiName } = getContext()

    switch (apiName as GQLApiNamesType) {
      case "REPORTING": {
        return getEnv("GRAPHQL_REPORTING_URI")
      }

      case "STUDY_PLAN":
      default:
        return getEnv("GRAPHQL_STUDYPLAN_URI")
    }
  }
})

let token = null

export async function createAndSetEmulatedAuthToken(
  email: string,
  password?: string
) {
  if (getEnv("EMULATE_AUTH") === "true") {
    //axios.get("http://google.com/")
    const customToken = (
      await axios.post(
        "https://us-central1-mp-ready-staging.cloudfunctions.net/authGroup-authUser",
        {
          email: email,
          password: password ?? email,
          app: getEnv("APPLICATION_ID")
        }
      )
    )?.data?.customToken
    if (!customToken) {
      console.log("custom token not found")
      return ""
    }

    const idToken = (
      await axios.post(
        "https://www.googleapis.com/identitytoolkit/v3/relyingparty/verifyCustomToken?key=" +
          getEnv("FIREBASE_KEY"),
        {
          returnSecureToken: true,
          token: customToken
        }
      )
    )?.data?.idToken
    token = idToken
    // console.log("auth response:" + JSON.stringify(idToken))
    return idToken
  } else {
    console.log("EMULATE_AUTH disabled")
    return ""
  }
}

async function getToken() {
  return token
}

const authLink = setContext(async (_, { headers: hdrs }) => {
  const headers = {
    ...hdrs,
    ...getCustomHeaders()
  }
  // get the authentication token from local storage if it exists //
  let token = null
  if (getEnv("USE_GQL_FIREBASE") === "true") {
    if (getEnv("EMULATE_AUTH") === "true") {
      token = await getToken()
    } else {
      token = await firebase.auth().currentUser?.getIdToken(false)
    }
  }
  // console.log("token: " + JSON.stringify(token))

  // console.log(
  //   "httpLink: " + JSON.stringify(getEnv("GRAPHQL_STUDYPLAN_URI"))
  // )

  if (!token) {
    //console.log(" token is not ready for apollo header, yet")
    return {
      headers // no change
    }
  }
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      "x-timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      authorization: token ? `Bearer ${token}` : "",
      "auth-provider": "firebase"
    }
  }
})

// start client
export const apolloCubeClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache()
})

// util function to retry 5x, needed for cube but not really needed for other connections which won't time out
export async function retry<T>(
  fn: () => Promise<T>,
  maxRetries = 5,
  minElapse = 5000
): Promise<T> {
  const startTime = Date.now()

  try {
    return await fn()
  } catch (error) {
    const elapsedTime = Date.now() - startTime

    if (maxRetries <= 0) {
      throw error
    }

    if (elapsedTime < minElapse) {
      await new Promise(resolve => setTimeout(resolve, minElapse - elapsedTime))
    }

    return retry(fn, maxRetries - 1, minElapse)
  }
}
